import { supabase } from "@/integrations/supabase/client";

/**
 * Fetch stored AI translations for one entity in one language.
 *
 * Safe to call from a route loader: it runs on the server during SSR (so the
 * translated text lands in the first HTML response, which is what search and
 * AI crawlers read) and on the client during navigation.
 *
 * Returns an empty map on any failure — callers fall back to English.
 */
export async function fetchEntityTranslations(
  entity: string,
  entityId: string | null | undefined,
  lang: string,
): Promise<Record<string, string>> {
  if (!entityId || lang === "en") return {};
  try {
    const { data, error } = await supabase
      .from("content_translations")
      .select("field, value")
      .eq("entity", entity)
      .eq("entity_id", entityId)
      .eq("lang", lang);
    if (error) return {};
    const map: Record<string, string> = {};
    for (const row of data ?? []) {
      if (typeof row.value === "string" && row.value.trim()) map[row.field] = row.value;
    }
    return map;
  } catch {
    return {};
  }
}

/**
 * Stored translations for many rows of one entity at once, keyed
 * `${entity_id}:${field}`. Used for menu dishes, where a page renders dozens
 * of records and one round-trip must cover them all.
 */
export async function fetchManyEntityTranslations(
  entity: string,
  entityIds: string[],
  lang: string,
): Promise<Record<string, string>> {
  const ids = Array.from(new Set(entityIds.filter(Boolean)));
  if (ids.length === 0 || lang === "en") return {};
  try {
    const { data, error } = await supabase
      .from("content_translations")
      .select("entity_id, field, value")
      .eq("entity", entity)
      .eq("lang", lang)
      .in("entity_id", ids);
    if (error) return {};
    const map: Record<string, string> = {};
    for (const row of data ?? []) {
      if (typeof row.value === "string" && row.value.trim()) map[`${row.entity_id}:${row.field}`] = row.value;
    }
    return map;
  } catch {
    return {};
  }
}

/** Language implied by a pathname, e.g. "/fi/place/x" -> "fi". */
export function langFromPathname(pathname: string | undefined): "fi" | "en" {
  return pathname?.startsWith("/fi/") || pathname === "/fi" ? "fi" : "en";
}

/** Pick a translated value, falling back to the English source. */
export function tr(
  map: Record<string, string>,
  field: string,
  fallback: string | null | undefined,
): string {
  const v = map[field];
  return typeof v === "string" && v.trim() ? v : (fallback ?? "");
}
